import { useEffect, useRef } from 'react';

/**
 * Sign out after a period of doing nothing.
 *
 * The server enforces the same limit when a session is extended, and that is the check that
 * matters — this one can be defeated by anybody willing to open the console. What it is for
 * is the screen: without it, a document stays open on an unattended monitor until somebody
 * touches the machine and a request finally fails. The point of an idle timeout is that the
 * screen clears while nobody is there.
 *
 * Activity is anything a person does — a key, a click, a scroll, a touch. Listeners are
 * passive and the work behind them is a single assignment, because these fire continuously
 * on a scroll and anything heavier would be felt.
 *
 * The deadline is kept in localStorage so several tabs agree: without that, a tab left open
 * in the background would sign out a person actively working in another one.
 */
const ACTIVITY = ['mousedown', 'keydown', 'wheel', 'touchstart', 'scroll'];
const DEADLINE_KEY = 'idh_idle_deadline';
const CHECK_MS = 15_000;

export default function useIdleTimeout(minutes, onTimeout) {
  const fired = useRef(false);
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  useEffect(() => {
    const limit = Number(minutes) || 0;
    if (limit <= 0) return undefined;

    const windowMs = limit * 60_000;
    fired.current = false;

    const extend = () => {
      try {
        localStorage.setItem(DEADLINE_KEY, String(Date.now() + windowMs));
      } catch {
        /* Private browsing, or storage refused. The interval below still works from memory. */
      }
      fallback = Date.now() + windowMs;
    };

    // Used when localStorage is unavailable, so the timeout degrades to per-tab rather than
    // to not happening at all.
    let fallback = Date.now() + windowMs;

    const deadline = () => {
      try {
        const stored = Number(localStorage.getItem(DEADLINE_KEY));
        // A stored deadline further out than one full window is a stale value from a longer
        // setting; trusting it would silently extend the session past the new limit.
        if (stored && stored <= Date.now() + windowMs) return stored;
      } catch {
        /* fall through to the in-memory copy */
      }
      return fallback;
    };

    extend();
    ACTIVITY.forEach((event) => window.addEventListener(event, extend, { passive: true }));

    // Polled rather than a single long timer: a laptop that sleeps for an hour does not fire
    // a timer set before it slept, and would wake up still signed in.
    const timer = setInterval(() => {
      if (fired.current || Date.now() < deadline()) return;
      fired.current = true;
      try {
        localStorage.removeItem(DEADLINE_KEY);
      } catch {
        /* nothing to clean up */
      }
      onTimeoutRef.current?.();
    }, CHECK_MS);

    return () => {
      clearInterval(timer);
      ACTIVITY.forEach((event) => window.removeEventListener(event, extend));
    };
  }, [minutes]);
}
